import { useMemo } from 'react';
import { useCodesHierarchy, flattenHierarchicalCodes } from './useCodesSidebar';
import { useCodeBarCollapseState } from './useCollapseState';
import { useCodesWithCollapse } from './useCodesWithCollapse';

/**
 * Hook that provides a flat list of codes with collapse state for the sidebar
 * When codes are collapsed, their children are hidden from the list
 * @param {string|number} projectId - Project ID
 * @param {boolean} ignoreCollapseState - If true, shows all codes regardless of collapse state
 * @returns {Object} Flat codes list with collapse state and loading status
 */
export function useCodeSidebarCodes(projectId, ignoreCollapseState = false) {
  // Get hierarchical codes from useCodesHierarchy (without any collapse state)
  const codesData = useCodesHierarchy(projectId);
  
  // Get code bar collapse state
  const collapseData = useCodeBarCollapseState(projectId);
  
  // Add collapse state to hierarchical codes using the shared hook
  const {
    codes: hierarchicalWithCollapse,
    isLoading,
    error
  } = useCodesWithCollapse(codesData, collapseData);
  
  // Flatten the hierarchical codes while respecting code bar collapse state
  const codes = useMemo(() => {
    if (!hierarchicalWithCollapse || hierarchicalWithCollapse.length === 0) {
      return [];
    }
    
    // Flatten while respecting collapse state (hiding children of collapsed codes)
    return flattenHierarchicalCodes(
      hierarchicalWithCollapse,
      (code) => ignoreCollapseState ? false : code.collapsed // Skip children if code is collapsed (unless ignoring)
    );
  }, [hierarchicalWithCollapse, ignoreCollapseState]);
  
  return {
    codes,
    isLoading,
    error
  };
}